<template>
  <div class="mb-12 mt-24 px-8 md:mt-9">
    <div
      class="mb-6 flex flex-col justify-center align-middle md:mb-0 md:flex-row md:justify-between"
    >
      <div>
        <h1 class="mb-1 flex self-center md:self-auto">Gebäudeübersicht</h1>
        <div class="mb-6 text-sm">
          <span v-if="businessEntity?.billingUnitReference" class="font-bold"
            >AE-NR.</span
          >
          {{ businessEntity?.billingUnitReference }}
          <span v-if="orderId" class="ml-3 font-bold">Auftragsnummer</span>
          {{ orderId }}
        </div>
      </div>
      <CloseOrderButton />
    </div>

    <div class="relative flex flex-row items-center">
      <BuildingCardCollection
        v-if="buildings.length > 0"
        :buildings="buildings"
      />
      <div
        v-if="isOnSiteInspection"
        class="m-2 flex h-16 w-[100px] shrink cursor-pointer flex-col justify-center rounded-xl border-dark-2 bg-light-1 py-5 text-center align-middle text-3xl text-primary-2 shadow hover:scale-[1.02] hover:bg-light-2 dark:border dark:bg-dark-1 hover:dark:bg-dark-2"
        data-testid="createBuildingButton"
        @click="addBuilding"
      >
        <em class="mdi mdi-plus"></em>
      </div>

      <div
        v-if="buildings.length > 0"
        class="ml-auto flex shrink-0 gap-1 rounded-lg bg-light-2 p-1 dark:bg-dark-2"
      >
        <button
          class="rounded-md p-2 text-2xl leading-none transition-colors"
          :class="
            activeView === 'floors'
              ? 'bg-primary-2 text-white'
              : 'hover:bg-light-3 dark:hover:bg-dark-3'
          "
          data-testid="buildingViewFloorsButton"
          @click="activeView = 'floors'"
        >
          <em class="mdi mdi-arrow-split-horizontal" />
        </button>
        <button
          class="rounded-md p-2 text-2xl leading-none transition-colors"
          :class="
            activeView === 'devices'
              ? 'bg-primary-2 text-white'
              : 'hover:bg-light-3 dark:hover:bg-dark-3'
          "
          data-testid="buildingViewDevicesButton"
          @click="activeView = 'devices'"
        >
          <em class="mdi mdi-format-list-bulleted" />
        </button>
      </div>
    </div>

    <div
      v-if="buildings.length > 0 && activeView === 'floors'"
      class="mt-10 flex flex-col gap-5"
      data-testid="floorTiles"
    >
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-4">
          <button
            v-if="isOnSiteInspection"
            class="rounded-lg bg-light-2 p-3 text-primary-2 dark:bg-dark-2"
            data-testId="openInspectedBuildingFormButton"
            @click="openInspectedBuildingForm"
          >
            <em class="mdi mdi-message-alert-outline mr-2"></em>
            <span>Materialplanung</span>
          </button>
          <BaseInformation
            v-if="buildingInformation"
            :status="buildingInformation.type"
            :information-text="buildingInformation.message"
          />
        </div>
        <div
          class="flex items-center rounded-lg bg-light-2 px-2 py-1 hover:bg-primary-2 hover:text-white dark:bg-dark-2 hover:dark:bg-primary-1"
          :class="{
            'bg-primary-2 text-white dark:bg-primary-1': isSwapModeActive,
          }"
          @click="toggleSwapMode"
        >
          <em class="mdi mdi-swap-horizontal text-3xl"></em>
        </div>
      </div>
      <div
        class="flex h-14 w-full flex-col justify-center rounded-xl border-dark-3 bg-light-1 text-center align-middle text-3xl text-primary-2 shadow hover:bg-light-2 dark:border dark:bg-dark-2 hover:dark:bg-dark-3"
        data-testid="addFloorOGButton"
        @click="addFloor('OG')"
      >
        <em class="mdi mdi-plus"></em>
      </div>

      <BuildingFloorLevel
        v-for="(roomGroupData, floorName) in buildingStruct"
        :key="floorName"
        data-testid="floorTile"
        :current-building="activeBuilding"
        :room-group-data="roomGroupData"
        :floor-name="floorName"
        :is-highest-floor-level="isHighestFloorLevel(floorName)"
        :room-group-data-maps="roomGroupDataMaps"
        :is-on-site-inspection="isOnSiteInspection"
      >
      </BuildingFloorLevel>
      <div
        class="flex h-14 w-full flex-col justify-center rounded-xl border-dark-3 bg-light-1 text-center align-middle text-3xl text-primary-2 shadow hover:bg-light-2 dark:border dark:bg-dark-2 hover:dark:bg-dark-3"
        data-testid="addFloorUGButton"
        @click="addFloor('UG')"
      >
        <em class="mdi mdi-plus"></em>
      </div>
    </div>

    <BuildingDevicesView
      v-if="buildings.length > 0 && activeView === 'devices'"
      class="mt-10"
      data-testid="buildingDevicesView"
      :building-struct="buildingStruct"
    />

    <div
      v-else-if="buildings.length === 0"
      class="mx-1 mt-10 rounded-lg bg-light-1 px-5 py-5 dark:bg-dark-1"
    >
      Diese Gewerbeeinheit ist leer. <br />
      Bitte lege das erste Gebäude mithilfe der
      <em class="mdi mdi-plus text-primary-2 dark:text-primary-2" />
      Schaltfläche an.
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, provide, ref } from 'vue';
import { useBuildingOverview } from '@/composables/useBuildingOverview';
import BuildingFloorLevel from '@/components/BuildingOverview/BuildingFloorLevel.vue';
import BuildingDevicesView from '@/components/BuildingOverview/BuildingDevicesView.vue';
import CloseOrderButton from '@/components/Header/CloseOrderButton.vue';
import BuildingCardCollection from '@/components/BuildingOverview/BuildingCardCollection.vue';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';

type BuildingView = 'floors' | 'devices';
const activeView = ref<BuildingView>('floors');

const {
  isSwapModeActive,
  activeBuilding,
  buildings,
  buildingStruct,
  businessEntity,
  orderId,
  isOnSiteInspection,
  buildingInformation,
  roomGroupDataMaps,

  initialize,
  toggleSwapMode,
  addFloor,
  addBuilding,
  openInspectedBuildingForm,
  isHighestFloorLevel,

  provideValues,
} = useBuildingOverview();

provide('isSwapModeActive', provideValues.isSwapModeActive);
provide('changeActiveBuilding', provideValues.changeActiveBuilding);
provide('editBuilding', provideValues.editBuilding);
provide('handleIsBuildingCompatible', provideValues.handleIsBuildingCompatible);
provide('roomGroupHandlers', provideValues.roomGroupHandlers);
provide('floorHandlers', provideValues.floorHandlers);
provide('isDarkMode', provideValues.isDarkMode);
onMounted(() => {
  initialize();
});
</script>
